import { useCallback, useEffect, useState } from "react";
import { useUnmount } from "react-use";
import { isEqual, uniqBy } from "lodash";
import useBlurController from "hooks/useBlurController";
import useMediaConstraints from "hooks/useMediaConstraints";
import useMediaDevicePermissions from "hooks/useMediaDevicePermissions";
import useWebRTC from "hooks/useWebRTC";
import {
  addTracksToStream,
  filterMediaDevices,
  getUserMediaWithFallback,
  getVideoTracks,
  removeTracksFromStream,
} from "lib/mediaStreamHelpers";
import { MediaConstraints } from "types";
import { defaultMediaConstraints } from "../../const";

export function useController() {
  const WebRTC = useWebRTC();
  const { blurStart, blurStop } = useBlurController();
  const { mediaConstraints, setMediaConstraints } = useMediaConstraints(defaultMediaConstraints);

  const [stream, setStream] = useState<MediaStream>(new MediaStream([]));
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);

  const setDevicesList = useCallback(async () => {
    try {
      const { audioInputList, videoInputList } = await WebRTC.getDeviceList();
      setAudioInputs(uniqBy(filterMediaDevices(audioInputList).reverse(), "groupId").reverse());
      setVideoInputs(filterMediaDevices(videoInputList));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [WebRTC]);

  const permissions = useMediaDevicePermissions(setDevicesList);

  const onAudioChange = async (constraints: MediaConstraints) => {
    if (constraints.audioEnabled) {
      if (!isEqual(mediaConstraints.audio, constraints.audio) || !stream.getAudioTracks().length) {
        removeTracksFromStream(stream, stream.getAudioTracks());
        const newStream = await getUserMediaWithFallback(
          { audio: constraints.audio },
          {
            onOverconstrained: () => {
              constraints.audio = true;
            },
            onNotAllowed: () => {
              constraints.audioEnabled = false;
            },
          }
        );
        addTracksToStream(stream, newStream.getAudioTracks());
        removeTracksFromStream(newStream, newStream.getTracks());
      } else {
        stream.getAudioTracks().forEach((t) => (t.enabled = true));
      }
    } else {
      stream.getAudioTracks().forEach((t) => (t.enabled = false));
    }

    return stream;
  };

  const onVideoChange = async (constraints: MediaConstraints) => {
    if (!isEqual(mediaConstraints.video, constraints.video) || !constraints.videoEnabled) {
      removeTracksFromStream(stream, getVideoTracks(stream));
    }
    if (constraints.videoEnabled) {
      const newStream = await getUserMediaWithFallback(
        { video: constraints.video },
        {
          onOverconstrained: () => {
            constraints.video = true;
          },
          onNotAllowed: () => {
            constraints.videoEnabled = false;
          },
        }
      );
      addTracksToStream(stream, getVideoTracks(newStream));
      removeTracksFromStream(newStream, newStream.getTracks());
    }

    return new MediaStream(stream.getTracks());
  };

  const onBlurChange = useCallback(
    async (constraints: MediaConstraints, outStream: MediaStream = stream) => {
      let data: MediaStream;

      if (constraints.blurEnabled) {
        const videoTracks = getVideoTracks(outStream);
        if (!videoTracks.length) {
          constraints.blurEnabled = false;
          await blurStop();
          return outStream;
        }
        data = await blurStart(videoTracks[0]);
      } else {
        data = await blurStop();
      }
      addTracksToStream(data, outStream.getAudioTracks());
      return data;
    },
    [blurStart, blurStop, stream]
  );

  const onChangeConstraints = async (constraints: MediaConstraints) => {
    try {
      setProcessing(true);
      let data: MediaStream | null = null;
      const localConstraints: MediaConstraints = {
        ...mediaConstraints,
        ...constraints,
      };
      if (!isEqual(mediaConstraints.blurEnabled, localConstraints.blurEnabled)) {
        data = await onBlurChange(localConstraints);
      } else if (
        !isEqual(mediaConstraints.audio, localConstraints.audio) ||
        !isEqual(mediaConstraints.audioEnabled, localConstraints.audioEnabled)
      ) {
        data = await onAudioChange(localConstraints);
      } else if (
        !isEqual(mediaConstraints.video, localConstraints.video) ||
        !isEqual(mediaConstraints.videoEnabled, localConstraints.videoEnabled)
      ) {
        if (localConstraints.blurEnabled) {
          await blurStop();
        }
        data = await onVideoChange(localConstraints);
        if (localConstraints.blurEnabled && localConstraints.videoEnabled) {
          data = await onBlurChange(localConstraints, data);
        }
      }

      if (data) {
        setMediaConstraints(localConstraints);
        setStream(data);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      onChangeConstraints({
        ...mediaConstraints,
      }).then();
    } finally {
      setProcessing(false);
    }
  };

  const closeStream = () => {
    WebRTC.closeAllStreams();
    stream.getTracks().forEach((t) => t.stop());
  };

  const onProduceStream = useCallback(async () => {
    try {
      setLoading(true);
      if (mediaConstraints.videoEnabled || mediaConstraints.audioEnabled) {
        let newStream = await getUserMediaWithFallback(
          {
            video: mediaConstraints.videoEnabled ? mediaConstraints.video : false,
            audio: mediaConstraints.audioEnabled ? mediaConstraints.audio : false,
          },
          {
            onOverconstrained: () => {
              setMediaConstraints({
                ...mediaConstraints,
                audio: mediaConstraints.audioEnabled ? true : mediaConstraints.audio,
                video: mediaConstraints.videoEnabled ? true : mediaConstraints.video,
              });
            },
            onNotAllowed: () => {
              setMediaConstraints({
                ...mediaConstraints,
                audioEnabled: false,
                videoEnabled: false,
              });
            },
          }
        );

        if (mediaConstraints.blurEnabled) {
          newStream = await onBlurChange({ ...mediaConstraints }, newStream);
        }
        setStream(newStream);
      }
    } finally {
      setLoading(false);
    }
  }, [mediaConstraints, onBlurChange, setMediaConstraints]);

  useEffect(() => {
    onProduceStream().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUnmount(closeStream);

  return {
    stream,
    mediaConstraints,
    permissions,
    audioInputs,
    videoInputs,
    onChangeConstraints,
    closeStream,
    processing,
    loading,
  };
}
